<template>
    <section class="snake-game">
        <div class="container">
            <h2>Snake</h2>
            <canvas id="snake-canvas" ref="gameCanvas" :width="canvasWidth" :height="canvasHeight"></canvas>
            <div class="controls" v-if="isMobile">
                <button @click="move('up')">Cima</button>
                <div class="horizontal-buttons">
                    <button @click="move('left')">Esquerda</button>
                    <button @click="move('right')">Direita</button>
                </div>
                <button @click="move('down')">Baixo</button>
            </div>
            <p>Pontuação: <span>{{ score }}</span></p>
        </div>
    </section>
</template>


<script setup>
import { ref, onMounted, onBeforeUnmount } from 'vue';
const canvasWidth = ref(300);
const canvasHeight = ref(300);
const grid = ref(16);
const count = ref(0);
const snake = ref({
    x: 160,
    y: 160,
    dx: 16,
    dy: 0,
    cells: [],
    maxCells: 4,
});
const apple = ref({
    x: 320,
    y: 320,
});
const score = ref(0);
const gameLoopInterval = ref(null);
const isMobile = ref(/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent));
const gameCanvas = ref(null);
let ctx = null;


const getRandomInt = (min, max) => {
    min = Math.ceil(min);
    max = Math.floor(max);
    return Math.floor(Math.random() * (max - min)) + min;
};

const createApple = () => {
    apple.value.x = getRandomInt(0, canvasWidth.value / grid.value) * grid.value;
    apple.value.y = getRandomInt(0, canvasHeight.value / grid.value) * grid.value;
};

const startGameLoop = () => {
    gameLoopInterval.value = setInterval(gameLoop, 100); 
};

const gameLoop = () => {
    if (++count.value < 10) {
        return;
    }
    count.value = 0;
    ctx.clearRect(0, 0, canvasWidth.value, canvasHeight.value);
    moveSnake();
    drawApple();
    drawSnake();
};

const moveSnake = () => {
    snake.value.x += snake.value.dx;
    snake.value.y += snake.value.dy;

    // Envolver a cobra nas bordas
    if (snake.value.x < 0) {
        snake.value.x = canvasWidth.value - grid.value;
    } else if (snake.value.x >= canvasWidth.value) {
        snake.value.x = 0;
    }
    if (snake.value.y < 0) {
        snake.value.y = canvasHeight.value - grid.value;
    } else if (snake.value.y >= canvasHeight.value) {
        snake.value.y = 0;
    }

    snake.value.cells.unshift({ x: snake.value.x, y: snake.value.y });
    if (snake.value.cells.length > snake.value.maxCells) {
        snake.value.cells.pop();
    }

    // Comer a maçã
    if (snake.value.x === apple.value.x && snake.value.y === apple.value.y) {
        snake.value.maxCells++;
        score.value++;
        createApple();
    }

    // Verificar colisão com o próprio corpo
    for (let i = 1; i < snake.value.cells.length; i++) {
        if (snake.value.x === snake.value.cells[i].x && snake.value.y === snake.value.cells[i].y) {
            resetGame();
            break;
        }
    }
};

const drawApple = () => {
    ctx.fillStyle = 'red';
    ctx.fillRect(apple.value.x, apple.value.y, grid.value - 1, grid.value - 1);
};

const drawSnake = () => {
    ctx.fillStyle = 'green';
    snake.value.cells.forEach((cell, index) => {
        ctx.fillRect(cell.x, cell.y, grid.value - 1, grid.value - 1);
        // Desenha a cabeça da cobra com uma cor diferente
        if (index === 0) {
            ctx.fillStyle = 'darkgreen';
            ctx.fillRect(cell.x, cell.y, grid.value - 1, grid.value - 1);
        }
    });
};

const resetGame = () => {
    snake.value.x = 160;
    snake.value.y = 160;
    snake.value.dx = grid.value;
    snake.value.dy = 0;
    snake.value.cells = [];
    snake.value.maxCells = 4;
    score.value = 0;
    createApple();
};

const handleKeyDown = (e) => {
    // Impede o scroll da página com as setas
    if (e.key === 'ArrowUp' || e.key === 'ArrowDown' || e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
        e.preventDefault();
    }

    if (e.key === 'ArrowUp' && snake.value.dy === 0) {
        snake.value.dy = -grid.value;
        snake.value.dx = 0;
    } else if (e.key === 'ArrowDown' && snake.value.dy === 0) {
        snake.value.dy = grid.value;
        snake.value.dx = 0;
    } else if (e.key === 'ArrowLeft' && snake.value.dx === 0) {
        snake.value.dx = -grid.value;
        snake.value.dy = 0;
    } else if (e.key === 'ArrowRight' && snake.value.dx === 0) {
        snake.value.dx = grid.value;
        snake.value.dy = 0;
    }
};

const move = (direction) => {
    if (direction === 'up' && snake.value.dy === 0) {
        snake.value.dy = -grid.value;
        snake.value.dx = 0;
    } else if (direction === 'down' && snake.value.dy === 0) {
        snake.value.dy = grid.value;
        snake.value.dx = 0;
    } else if (direction === 'left' && snake.value.dx === 0) {
        snake.value.dx = -grid.value;
        snake.value.dy = 0;
    } else if (direction === 'right' && snake.value.dx === 0) {
        snake.value.dx = grid.value;
        snake.value.dy = 0;
    }
};

onMounted(() => {
    const canvas = gameCanvas.value;
    if (canvas) {
        ctx = canvas.getContext('2d');
        createApple();
        startGameLoop();
        window.addEventListener('keydown', handleKeyDown);
    }
});

onBeforeUnmount(() => {
    clearInterval(gameLoopInterval.value);
    window.removeEventListener('keydown', handleKeyDown);
});
</script>


<style scoped>
.snake-game {
    border: 2px solid gray;
    min-height: 20vh;

    canvas {
        border: 2px solid #333;
        margin-bottom: 10px;
        background-color: #eee;
    }

    .container {
        background-color: #fff;
        padding: 20px;
        border-radius: 8px;
        box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
        text-align: center;

        .controls {
            display: flex;
            flex-direction: column;
            align-items: center;
            margin-bottom: 10px;
        }

        .horizontal-buttons {
            display: flex;
        }

        .controls button {
            padding: 10px 20px;
            margin: 5px;
            border: none;
            border-radius: 5px;
            background-color: #007bff;
            color: white;
            cursor: pointer;
            font-size: 16px;
        }

        .controls button:hover {
            background-color: #0056b3;
        }
    }
}
</style>